import { Dialog, Transition } from "@headlessui/react";
import { ArrowPathIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import React, { Fragment, useEffect, useRef, useState } from "react";
import config from "../../../config";
import { useToast } from "../../../hooks/ToastContext";

function ProfilePopup({ isOpen = false, onClose, title, parseMember, id }) {
  const cancelButtonRef = useRef(null);
  const { showToast } = useToast();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    if (parseMember) {
      setProfile((prev) => ({
        ...prev,
        name: parseMember.name || "",
        email: parseMember.email || "",
        phone: parseMember.phone || "",
        password: "",
        confirm_password: "",
      }));
    }
  }, [parseMember]);

  const updateProfile = async () => {
    let payload = {
      contest_member_id: parseMember?.contest_member_id,
      name: profile?.name,
    };
    if (profile?.phone) {
      payload.phone = profile.phone;
    }

    if (profile?.password) {
      payload.password = profile.password;
      payload.confirm_password = profile.confirm_password;
    }

    try {
      const res = await axios.post(
        `${config.API_URL}/contest-member-update`,
        payload
      );

      if (res.status === 200) {
        showToast("success", res.data.message);

        const updatedMember = {
          ...parseMember,
          name: profile.name,
          phone: profile.phone,
        };

        sessionStorage.setItem("member_" + id, JSON.stringify(updatedMember));

        const updatedData = JSON.parse(sessionStorage.getItem("member_" + id));
        Object.assign(parseMember, updatedData);
      }
    } catch (error) {
      console.error("Error updating profile:", error);

      if (error.response) {
        // Handle validation errors
        if (error.response.status === 422) {
          const errors = error.response.data.errors;
          const errorMessages = Object.values(errors).flat().join("\n");

          showToast("error", errorMessages);
        } else {
          showToast(
            "error",
            error.response.data.message || "Failed to update profile."
          );
        }
      } else if (error.request) {
        showToast("error", "No response from the server. Please try again.");
      } else {
        showToast("error", "Unexpected error occurred.");
      }
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-xl">
                <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
                  {/* Header Section */}
                  <div className="flex items-center justify-between border-b pb-3">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-bold text-gray-900 flex items-center gap-3"
                    >
                      <UserCircleIcon className="w-10 h-10 text-gray-600" />
                      <div>
                        <h2>{title || "Profile"}</h2>
                        <p className="text-sm text-gray-500">
                          Manage your account details
                        </p>
                      </div>
                    </Dialog.Title>
                    <button
                      type="button"
                      className="absolute right-3 top-3 rounded-md bg-gray-200 p-1 text-gray-500 hover:bg-gray-300"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon
                        className="h-5 w-5 text-black"
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {/* Profile Form */}
                  <div className="mt-4 space-y-4">
                    {/* Name Field (Editable) */}
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="name"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        value={profile?.name}
                        name="name"
                        id="name"
                        placeholder="Enter Your Name"
                        onChange={(e) =>
                          setProfile({ ...profile, name: e.target.value })
                        }
                        className="mt-1 block input_focus_primary w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        autocomplete="off"
                      />
                    </div>

                    {/* Email Field (Read-Only) */}
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="email"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        value={profile?.email}
                        name="email"
                        id="email"
                        placeholder="Enter Your Email"
                        readOnly
                        className="mt-1 block input_focus_primary w-full rounded-md border-gray-300 shadow-sm sm:text-sm bg-gray-100 cursor-not-allowed"
                      />
                    </div>
                    {/* Phone Field */}
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="phone"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        inputmode="tel"
                        pattern="[0-9]*"
                        maxlength="14"
                        name="phone"
                        id="phone"
                        placeholder="Enter Your Phone"
                        value={profile?.phone}
                        onChange={(e) =>
                          setProfile({ ...profile, phone: e.target.value })
                        }
                        className="mt-1 block input_focus_primary w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        autocomplete="tel"
                        autocorrect="off"
                      />
                    </div>

                    {/* Password Field */}
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="password"
                      >
                        New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter new password"
                        name="password"
                        id="password"
                        value={profile?.password}
                        onChange={(e) =>
                          setProfile({ ...profile, password: e.target.value })
                        }
                        className="mt-1 block input_focus_primary w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>

                    {/*Confirm Password Field */}
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="confirm_password"
                      >
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter confirm password"
                        name="confirm_password"
                        id="confirm_password"
                        value={profile?.confirm_password}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            confirm_password: e.target.value,
                          })
                        }
                        className="mt-1 block input_focus_primary w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="mt-6 flex justify-center items-center">
                    <button
                      type="button"
                      onClick={updateProfile}
                      className="primary_btn_bg flex items-center rounded-md px-4 py-2 text-white font-medium hover:bg-blue-700"
                    >
                      <span className="mr-2">
                        <ArrowPathIcon width={20} />
                      </span>{" "}
                      Update
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ProfilePopup;
